import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, CheckCircle2, RotateCcw, CalendarClock, Repeat, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { KIND_META, STATUS_STYLE, longDate, recurrenceText } from "@/lib/calendar";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const Reschedule = ({ o, onDone }) => {
  const [d, setD] = useState(o.date);
  const [t, setT] = useState(o.time || "");
  const save = async () => {
    try {
      await api.post(`/calendar/items/${o.item_id}/reschedule`, { date: o.date, new_date: d, new_time: t || null });
      toast.success("Reagendado"); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha ao reagendar"); }
  };
  return (
    <div className="flex gap-2 items-end rounded-xl bg-slate-50 p-2.5" data-testid="cal-reschedule-box">
      <Input type="date" value={d} onChange={(e) => setD(e.target.value)} className="h-9" data-testid="cal-resched-date" />
      <Input type="time" value={t} onChange={(e) => setT(e.target.value)} className="h-9 w-28" data-testid="cal-resched-time" />
      <Button onClick={save} disabled={!d || d === o.date} className="h-9" data-testid="cal-resched-save">OK</Button>
    </div>
  );
};

export default function OccurrenceSheet({ occ, onOpenChange, onChanged, onEdit }) {
  const [obs, setObs] = useState("");
  const [resched, setResched] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  useEffect(() => { setObs(occ?.observation || ""); setResched(false); setConfirmDel(false); }, [occ]);
  if (!occ) return null;
  const st = STATUS_STYLE[occ.status];
  const recurring = occ.freq && occ.freq !== "none";
  const close = () => onOpenChange(false);
  const done = () => { close(); onChanged(); };

  const complete = async (v) => {
    try {
      await api.post(`/calendar/items/${occ.item_id}/complete`, { date: occ.date, done: v, observation: obs });
      toast.success(v ? "Concluído" : "Reaberto"); done();
    } catch { toast.error("Falha ao salvar"); }
  };
  const saveObs = async () => {
    try { await api.post(`/calendar/items/${occ.item_id}/complete`, { date: occ.date, observation: obs }); toast.success("Observação salva"); done(); }
    catch { toast.error("Falha ao salvar"); }
  };
  const remove = async (scope) => {
    try { await api.delete(`/calendar/items/${occ.item_id}`, { params: { scope, date: occ.date } }); toast.success("Excluído"); done(); }
    catch { toast.error("Falha ao excluir"); }
  };

  return (
    <Dialog open={!!occ} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[92vh] overflow-y-auto" data-testid="cal-occ-sheet">
        <div className="flex items-start gap-3 pr-6">
          <span className="w-4 h-4 rounded mt-1.5 shrink-0" style={{ background: st.bg }} />
          <div className="min-w-0 flex-1">
            <DialogTitle className="text-xl font-normal leading-snug break-words" data-testid="cal-occ-title">{occ.title}</DialogTitle>
            <p className="text-sm text-slate-600 mt-1 capitalize">{longDate(occ.date)}</p>
            {occ.time && <p className="text-sm text-slate-600 flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{occ.time}{occ.end_time ? ` – ${occ.end_time}` : ""}</p>}
            {recurring && <p className="text-sm text-slate-600 flex items-center gap-1"><Repeat className="w-3.5 h-3.5" />{recurrenceText(occ)}</p>}
            <div className="flex gap-1.5 mt-2">
              <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full text-white" style={{ background: KIND_META[occ.kind]?.color }}>{KIND_META[occ.kind]?.label}</span>
              <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full" style={{ background: st.bg, color: st.fg }} data-testid="cal-occ-status">{st.label}</span>
            </div>
          </div>
        </div>
        {occ.notes && <p className="text-sm text-slate-700 whitespace-pre-wrap break-words bg-slate-50 rounded-xl p-3">{occ.notes}</p>}

        <div className="space-y-2">
          <span className="block text-[11px] font-bold text-slate-500">Observação</span>
          <Textarea rows={2} value={obs} onChange={(e) => setObs(e.target.value)} placeholder="Ex.: pago via pix, comprovante enviado" data-testid="cal-occ-obs" />
          {obs !== (occ.observation || "") && <Button variant="outline" size="sm" onClick={saveObs} data-testid="cal-occ-obs-save">Salvar observação</Button>}
        </div>

        {occ.status === "done"
          ? <Button variant="outline" onClick={() => complete(false)} className="w-full h-11 rounded-full" data-testid="cal-occ-reopen"><RotateCcw className="w-4 h-4 mr-1.5" />Reabrir</Button>
          : <Button onClick={() => complete(true)} className="w-full h-11 rounded-full bg-[#33b679] hover:bg-[#2a9d67] text-white font-bold" data-testid="cal-occ-complete"><CheckCircle2 className="w-4 h-4 mr-1.5" />Marcar como concluído</Button>}

        <div className="grid grid-cols-3 gap-2">
          <Button variant="outline" onClick={() => setResched((v) => !v)} data-testid="cal-occ-reschedule"><CalendarClock className="w-4 h-4 mr-1" />Reagendar</Button>
          <Button variant="outline" onClick={() => { close(); onEdit(occ); }} data-testid="cal-occ-edit"><Pencil className="w-4 h-4 mr-1" />Editar</Button>
          <Button variant="outline" onClick={() => (recurring ? setConfirmDel((v) => !v) : remove("all"))} className="text-red-600" data-testid="cal-occ-delete"><Trash2 className="w-4 h-4 mr-1" />Excluir</Button>
        </div>
        {resched && <Reschedule o={occ} onDone={done} />}
        {confirmDel && (
          <div className="grid grid-cols-2 gap-2" data-testid="cal-occ-delete-confirm">
            <Button variant="outline" onClick={() => remove("one")} data-testid="cal-del-one">Só este dia</Button>
            <Button onClick={() => remove("all")} className="bg-red-600 hover:bg-red-700 text-white" data-testid="cal-del-all">Todos os dias</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
